import React, { useEffect, useMemo, useState } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Slider from "react-slick";
import dev1 from "./assets/dev1.jpg";
import galloImg from "./assets/dev2.png";
import accountsImg from "./assets/dev3.png";
import greenSunImg from "./assets/dev4.png";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState("Frontend Development");
  const [animatedSkills, setAnimatedSkills] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  const [typedContact, setTypedContact] = useState("");
  const [typedTitle, setTypedTitle] = useState("");

  const titleText = "Building modern digital experiences that feel premium.";

  const projects = useMemo(
    () => [
      {
        title: "Shalom Schools Portal",
        description:
          "A modern student portal with role-based access, reports, fees, subjects, and notifications.",
        tech: ["React", "Node.js", "PostgreSQL", "Vercel"],
        link: "https://shalomschoolsmurewa.eiffeltech.co.zw/",
        badge: "Live",
        image: galloImg,
      },
      {
        title: "UZ Accommodation Portal",
        description:
          "A student-focused accommodation platform for searching and exploring off-campus housing.",
        tech: ["React", "Node.js", "Express", "Vercel"],
        link: "https://accountsoncall.com",
        badge: "Featured",
        image: accountsImg,
      },
      {
        title: "Shalom Schools Website",
        description:
          "An elegant school website built to showcase the institution, brand, and services.",
        tech: ["Next.js", "React", "Vercel"],
        link: "https://shalomgroupofschools.eiffeltech.co.zw/",
        badge: "Live",
        image: greenSunImg,
      },
    ],
    []
  );

  const contactTexts = useMemo(
    () => [
      "something amazing",
      "something powerful",
      "something beautiful",
      "something unforgettable",
    ],
    []
  );

  const skillsData = useMemo(
    () => ({
      "Frontend Development": [
        { name: "React", level: 95 },
        { name: "Next.js", level: 92 },
        { name: "JavaScript", level: 94 },
        { name: "TypeScript", level: 86 },
        { name: "Tailwind CSS", level: 90 },
        { name: "HTML / CSS", level: 97 },
      ],
      "Backend Development": [
        { name: "Node.js", level: 92 },
        { name: "Express.js", level: 90 },
        { name: "Java", level: 88 },
        { name: "Spring Boot", level: 84 },
        { name: "Python", level: 82 },
        { name: "REST APIs", level: 93 },
      ],
      "Database & Tools": [
        { name: "PostgreSQL", level: 90 },
        { name: "SQL Server", level: 84 },
        { name: "Git & GitHub", level: 95 },
        { name: "Postman", level: 92 },
        { name: "Docker", level: 78 },
        { name: "Vercel", level: 91 },
      ],
      "Mobile & Design": [
        { name: "Flutter", level: 94 },
        { name: "React Native", level: 82 },
        { name: "Figma", level: 88 },
        { name: "Responsive Design", level: 95 },
        { name: "UI/UX Thinking", level: 90 },
      ],
    }),
    []
  );

  const statsData = useMemo(
    () => ({
      "Frontend Development": {
        technologies: "12+",
        categories: "4",
        avg: "94%",
        years: "3+",
      },
      "Backend Development": {
        technologies: "10+",
        categories: "4",
        avg: "88%",
        years: "3+",
      },
      "Database & Tools": {
        technologies: "10+",
        categories: "3",
        avg: "88%",
        years: "3+",
      },
      "Mobile & Design": {
        technologies: "8+",
        categories: "3",
        avg: "90%",
        years: "2+",
      },
    }),
    []
  );

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    arrows: false,
  };

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let textIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeout;

    const type = () => {
      const currentText = contactTexts[textIndex];

      if (!isDeleting) {
        charIndex += 1;
        setTypedContact(currentText.substring(0, charIndex));

        if (charIndex === currentText.length) {
          isDeleting = true;
          timeout = setTimeout(type, 2000);
          return;
        }
      } else {
        charIndex -= 1;
        setTypedContact(currentText.substring(0, charIndex));

        if (charIndex === 0) {
          isDeleting = false;
          textIndex = (textIndex + 1) % contactTexts.length;
        }
      }

      timeout = setTimeout(type, isDeleting ? 40 : 60);
    };

    type();
    return () => clearTimeout(timeout);
  }, [contactTexts]);

  useEffect(() => {
    let index = 0;
    let isDeleting = false;
    let timeout;

    const type = () => {
      if (!isDeleting) {
        index += 1;
        setTypedTitle(titleText.slice(0, index));

        if (index === titleText.length) {
          isDeleting = true;
          timeout = setTimeout(type, 5000);
          return;
        }
      } else {
        index -= 1;
        setTypedTitle(titleText.slice(0, index));

        if (index === 0) {
          isDeleting = false;
        }
      }

      timeout = setTimeout(type, isDeleting ? 90 : 40);
    };

    type();
    return () => clearTimeout(timeout);
  }, [titleText]);

  useEffect(() => {
    setAnimatedSkills(
      skillsData[activeCategory].map((skill) => ({ ...skill, level: 0 }))
    );

    const timer = setTimeout(() => {
      setAnimatedSkills(skillsData[activeCategory]);
    }, 120);

    return () => clearTimeout(timer);
  }, [activeCategory, skillsData]);

  const scrollTo = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setMenuOpen(false);
    }
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <div style={styles.page}>
      <style>{`
        :root {
          --bg: #07111f;
          --bg-soft: #0c1728;
          --text: #ffffff;
          --text-soft: #a8b3c7;
          --text-muted: #8fa0b8;
          --card: rgba(255,255,255,0.06);
          --card-2: rgba(255,255,255,0.04);
          --border: rgba(255,255,255,0.1);
          --border-2: rgba(255,255,255,0.08);
          --nav-bg: rgba(9, 18, 32, 0.72);
          --shadow: 0 12px 35px rgba(0,0,0,0.25);
          --primary: #14b8a6;
          --secondary: #2563eb;
          --accent: #5eead4;
          --icon: #7dd3fc;
          --overlay-1: linear-gradient(
            90deg,
            rgba(3, 10, 24, 0.92) 0%,
            rgba(4, 16, 37, 0.8) 35%,
            rgba(4, 16, 37, 0.58) 60%,
            rgba(4, 16, 37, 0.72) 100%
          );
          --overlay-2: radial-gradient(circle at left center, rgba(0, 40, 120, 0.28), transparent 45%);
        }

        [data-theme="light"] {
          --bg: #f6f9ff;
          --bg-soft: #ffffff;
          --text: #0f172a;
          --text-soft: #475569;
          --text-muted: #64748b;
          --card: rgba(255,255,255,0.85);
          --card-2: rgba(255,255,255,0.72);
          --border: rgba(15,23,42,0.10);
          --border-2: rgba(15,23,42,0.08);
          --nav-bg: rgba(255,255,255,0.82);
          --shadow: 0 16px 40px rgba(15,23,42,0.10);
          --primary: #0ea5e9;
          --secondary: #2563eb;
          --accent: #0f766e;
          --icon: #2563eb;
          --overlay-1: linear-gradient(
            90deg,
            rgba(255,255,255,0.86) 0%,
            rgba(255,255,255,0.66) 35%,
            rgba(255,255,255,0.35) 60%,
            rgba(255,255,255,0.55) 100%
          );
          --overlay-2: radial-gradient(circle at left center, rgba(59,130,246,0.12), transparent 45%);
        }

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
          scroll-behavior: smooth;
        }

        html, body, #root {
          min-height: 100%;
        }

        body {
          background: var(--bg);
          color: var(--text);
          font-family: Inter, Arial, sans-serif;
          transition: background 0.35s ease, color 0.35s ease;
        }

        a {
          text-decoration: none;
          color: inherit;
        }

        button {
          font-family: inherit;
        }

        .container {
          width: min(1180px, 92%);
          margin: 0 auto;
        }

        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 14px;
          color: var(--text);
        }

        .section-subtitle {
          color: var(--text-soft);
          max-width: 700px;
          font-size: 1rem;
          line-height: 1.8;
          margin: 0 auto 40px auto;
        }

        .glass {
          background: var(--card);
          border: 1px solid var(--border);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          box-shadow: var(--shadow);
        }

        .navbar {
          position: sticky;
          top: 0;
          z-index: 1000;
          width: 100%;
          transition: all 0.3s ease;
        }

        .navbar-inner {
          width: min(1180px, 92%);
          margin: 16px auto 0 auto;
          padding: 16px 22px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          background: var(--nav-bg);
          border: 1px solid var(--border-2);
          backdrop-filter: blur(18px);
          box-shadow: var(--shadow);
        }

        .navbar.scrolled .navbar-inner {
          margin-top: 10px;
        }

        .logo {
          font-size: 1.1rem;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: var(--text);
          white-space: nowrap;
        }

        .logo span {
          color: var(--accent);
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 28px;
          list-style: none;
        }

        .nav-links li button {
          background: none;
          border: none;
          color: var(--text);
          font-size: 0.95rem;
          cursor: pointer;
          transition: 0.3s ease;
        }

        .nav-links li button:hover {
          color: var(--accent);
        }

        .nav-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .theme-toggle {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          border: 1px solid var(--border);
          background: var(--card);
          color: var(--text);
          cursor: pointer;
          display: grid;
          place-items: center;
          font-size: 1rem;
          transition: 0.3s ease;
          backdrop-filter: blur(12px);
        }

        .theme-toggle:hover {
          transform: translateY(-2px);
          color: var(--accent);
        }

        .nav-cta {
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          color: white;
          border: none;
          padding: 12px 18px;
          border-radius: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.3s ease;
        }

        .nav-cta:hover {
          transform: translateY(-2px);
        }

        .hamburger {
          display: none;
          flex-direction: column;
          gap: 5px;
          cursor: pointer;
        }

        .hamburger span {
          width: 24px;
          height: 2.5px;
          background: var(--text);
          border-radius: 10px;
        }

        .hero {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 120px 0 60px;
        }

        .hero-bg {
          position: absolute;
          inset: 0;
          z-index: 1;
        }

        .hero-bg-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          filter: scale(1);
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: var(--overlay-1);
        }

        .hero-overlay-2 {
          position: absolute;
          inset: 0;
          background: var(--overlay-2);
        }

        .hero-content-wrap {
          position: relative;
          z-index: 2;
        }

        .modern-hero {
          max-width: 760px;
          padding-top: 40px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 18px;
          border-radius: 999px;
          background: var(--card);
          border: 1px solid var(--border);
          color: var(--text);
          font-size: 0.95rem;
          font-weight: 700;
          backdrop-filter: blur(12px);
          margin-bottom: 24px;
        }

        .hero-title {
          font-size: clamp(3rem, 6vw, 5.2rem);
          line-height: 1.02;
          font-weight: 900;
          margin-bottom: 20px;
          max-width: 700px;
          letter-spacing: -1.6px;
          background: linear-gradient(
            120deg,
            var(--text) 0%,
            #67e8f9 18%,
            #60a5fa 35%,
            #a78bfa 52%,
            #22d3ee 68%,
            var(--text) 85%,
            #67e8f9 100%
          );
          background-size: 300% 300%;
          background-position: 0% 50%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          color: transparent;
          animation: heroTextFlow 6s ease-in-out infinite;
        }

        @keyframes heroTextFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .cursor {
          margin-left: 4px;
          color: #67e8f9;
          animation: blink 0.8s infinite;
        }

        @keyframes blink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0; }
        }

        .hero-text {
          font-size: 1.08rem;
          line-height: 1.85;
          color: var(--text);
          max-width: 680px;
          margin-bottom: 30px;
          opacity: 0.92;
        }

        .hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin-bottom: 38px;
        }

        .btn-primary,
        .btn-secondary,
        .btn-mini {
          border: none;
          cursor: pointer;
          transition: 0.3s ease;
        }

        .btn-primary,
        .btn-mini {
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          color: white;
          padding: 15px 22px;
          border-radius: 14px;
          font-weight: 700;
        }

        .btn-secondary {
          background: transparent;
          color: var(--text);
          padding: 15px 22px;
          border-radius: 14px;
          border: 1px solid var(--border);
          font-weight: 700;
        }

        .btn-primary:hover,
        .btn-secondary:hover,
        .btn-mini:hover {
          transform: translateY(-2px);
        }

        .hero-bottom-cards {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 220px));
          gap: 18px;
        }

        .hero-glass-card {
          padding: 22px 20px;
          border-radius: 22px;
          background: var(--card);
          border: 1px solid var(--border);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          box-shadow: var(--shadow);
        }

        .hero-glass-card h3 {
          font-size: 2rem;
          margin: 0 0 8px;
          color: var(--text);
          font-weight: 800;
        }

        .hero-glass-card p {
          margin: 0;
          color: var(--text-soft);
          line-height: 1.55;
          font-size: 0.95rem;
        }

        .section {
          padding: 90px 0;
        }

        .about-grid,
        .services-grid,
        .info-grid,
        .footer-grid,
        .contact-grid {
          display: grid;
          gap: 22px;
        }

        .about-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .about-card,
        .service-card,
        .info-card,
        .contact-card,
        .skills-menu,
        .skills-panel,
        .quote-box {
          border-radius: 24px;
        }

        .about-card,
        .service-card,
        .info-card,
        .contact-card {
          padding: 28px;
        }

        .about-card h3,
        .service-card h3,
        .info-card h3 {
          font-size: 1.1rem;
          margin-bottom: 14px;
          color: var(--text);
        }

        .about-card p,
        .service-card p,
        .info-card p {
          color: var(--text-soft);
          line-height: 1.8;
        }

        .services-grid {
          grid-template-columns: repeat(4, 1fr);
        }

        .service-card,
        .project-card,
        .info-card,
        .about-card {
          transition: 0.3s ease;
        }

        .service-card:hover,
        .project-card:hover,
        .info-card:hover,
        .about-card:hover {
          transform: translateY(-6px);
        }

        .service-icon {
          width: 58px;
          height: 58px;
          display: grid;
          place-items: center;
          border-radius: 16px;
          margin-bottom: 18px;
          font-size: 1.35rem;
          background: linear-gradient(135deg, rgba(20,184,166,0.18), rgba(37,99,235,0.2));
          color: var(--icon);
        }

        .skills-layout {
          display: grid;
          grid-template-columns: 270px 1fr;
          gap: 24px;
          align-items: start;
        }

        .skills-menu {
          padding: 18px;
        }

        .skills-menu button {
          width: 100%;
          text-align: left;
          margin-bottom: 12px;
          border: none;
          padding: 15px 16px;
          border-radius: 16px;
          background: transparent;
          color: var(--text);
          font-size: 0.96rem;
          cursor: pointer;
          transition: 0.3s ease;
        }

        .skills-menu button.active {
          background: linear-gradient(135deg, rgba(20,184,166,0.2), rgba(37,99,235,0.22));
          border: 1px solid rgba(94,234,212,0.2);
          color: var(--text);
        }

        .skills-panel {
          padding: 26px;
        }

        .skill-item {
          margin-bottom: 20px;
        }

        .skill-top {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
          font-size: 0.96rem;
          color: var(--text);
        }

        .skill-bar {
          width: 100%;
          height: 12px;
          border-radius: 999px;
          background: var(--card-2);
          overflow: hidden;
        }

        .skill-fill {
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(135deg, #14b8a6, #2563eb, #8b5cf6);
          transition: width 0.8s ease;
        }

        .stats-row {
          margin-top: 24px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          padding: 18px;
          border-radius: 26px;
        }

        .stat-box {
          padding: 18px;
          border-radius: 18px;
          background: var(--card-2);
          text-align: center;
          border: 1px solid var(--border-2);
        }

        .stat-box h4 {
          font-size: 1.45rem;
          margin-bottom: 6px;
          color: var(--text);
        }

        .stat-box p {
          color: var(--text-soft);
          font-size: 0.9rem;
        }

        .projects-wrap {
          margin-top: 24px;
        }

        .project-card {
          overflow: hidden;
          border-radius: 28px;
          cursor: pointer;
        }

        .project-image-wrap {
          position: relative;
          height: 270px;
          overflow: hidden;
        }

        .project-image-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }

        .project-card:hover .project-image-wrap img {
          transform: scale(1.06);
        }

        .project-badge {
          position: absolute;
          top: 18px;
          left: 18px;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(7,17,31,0.8);
          color: #ffffff;
          font-size: 0.82rem;
          border: 1px solid rgba(255,255,255,0.1);
        }

        .project-body {
          padding: 24px;
        }

        .project-body h3 {
          margin-bottom: 12px;
          font-size: 1.2rem;
          color: var(--text);
        }

        .project-body p {
          color: var(--text-soft);
          line-height: 1.8;
          margin-bottom: 18px;
        }

        .tech-stack {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 18px;
        }

        .tech-stack span {
          padding: 8px 12px;
          border-radius: 999px;
          background: var(--card-2);
          font-size: 0.84rem;
          color: var(--text);
          border: 1px solid var(--border-2);
        }

        .quote-box {
          padding: 38px;
          text-align: center;
        }

        .quote-box p {
          font-size: clamp(1.1rem, 2vw, 1.5rem);
          line-height: 1.8;
          color: var(--text);
          max-width: 840px;
          margin: 0 auto 14px auto;
        }

        .quote-box h4 {
          color: var(--accent);
          font-weight: 600;
        }

        .info-grid {
          grid-template-columns: repeat(3, 1fr);
        }

        .contact-grid {
          grid-template-columns: 1.1fr 0.9fr;
          align-items: start;
        }

        .contact-copy {
          padding-right: 18px;
        }

        .contact-pill {
          display: inline-block;
          padding: 10px 14px;
          border-radius: 999px;
          background: rgba(94, 234, 212, 0.08);
          border: 1px solid rgba(94,234,212,0.16);
          color: var(--accent);
          margin-bottom: 18px;
          font-size: 0.92rem;
          font-weight: 600;
        }

        .contact-title {
          font-size: clamp(2.2rem, 5vw, 4rem);
          line-height: 1.05;
          font-weight: 900;
          margin-bottom: 18px;
          color: var(--text);
        }

        .contact-title span {
          background: linear-gradient(135deg, #5eead4, #60a5fa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .contact-copy p {
          color: var(--text-soft);
          line-height: 1.9;
          margin-bottom: 24px;
          max-width: 620px;
        }

        .contact-list {
          display: grid;
          gap: 14px;
        }

        .contact-list div {
          display: flex;
          align-items: center;
          gap: 12px;
          color: var(--text);
        }

        .contact-list i {
          color: var(--icon);
          width: 20px;
        }

        .contact-card h3 {
          font-size: 1.2rem;
          margin-bottom: 8px;
          color: var(--text);
        }

        .contact-card p {
          color: var(--text-soft);
          margin-bottom: 18px;
        }

        .contact-actions {
          display: grid;
          gap: 14px;
          margin-bottom: 22px;
        }

        .contact-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 15px 18px;
          border-radius: 14px;
          font-weight: 700;
          transition: 0.3s ease;
        }

        .contact-link:hover {
          transform: translateY(-2px);
        }

        .contact-link.primary {
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          color: white;
        }

        .contact-link.secondary {
          background: var(--card-2);
          border: 1px solid var(--border);
          color: var(--text);
        }

        .contact-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .contact-stats div {
          padding: 16px;
          border-radius: 18px;
          text-align: center;
          background: var(--card-2);
          border: 1px solid var(--border-2);
        }

        .contact-stats strong {
          display: block;
          font-size: 1.2rem;
          margin-bottom: 6px;
          color: var(--text);
        }

        .contact-stats span {
          color: var(--text-soft);
          font-size: 0.88rem;
        }

        .footer {
          padding: 35px 0 50px;
        }

        .footer-grid {
          grid-template-columns: 1.2fr 1fr 1fr 1fr;
          padding: 28px;
          border-radius: 28px;
        }

        .footer-brand h3 {
          font-size: 1.4rem;
          margin-bottom: 14px;
          color: var(--text);
        }

        .footer-brand p,
        .footer-links p,
        .footer-links a {
          color: var(--text-soft);
          line-height: 1.8;
          font-size: 0.95rem;
        }

        .footer-links h4 {
          margin-bottom: 14px;
          font-size: 1rem;
          color: var(--text);
        }

        .footer-socials {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
        }

        .footer-socials a {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          background: var(--card-2);
          border: 1px solid var(--border);
          transition: 0.3s ease;
          color: var(--text);
        }

        .footer-socials a:hover {
          transform: translateY(-3px);
          color: var(--accent);
        }
                   .footer-bottom {
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  font-size: 13px;
  color: #aaa;
  text-align: center;
}

.footer-line {
  width: 100px;
  height: 2px;
  background: #d4af37;
  border-radius: 10px;
}

.powered {
  color: #888;
  font-size: 13px;
}

.powered strong {
  color: #d4af37;
  font-weight: 700;
}

        .copyright {
          text-align: center;
          color: var(--text-muted);
          margin-top: 22px;
          font-size: 0.92rem;
        }
.footer-line {
  width: 100px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #d4af37, transparent);
}
        @media (max-width: 1024px) {
          .contact-grid,
          .skills-layout,
          .services-grid,
          .info-grid,
          .about-grid,
          .footer-grid {
            grid-template-columns: 1fr;
          }

          .stats-row,
          .contact-stats,
          .hero-bottom-cards {
            grid-template-columns: repeat(2, 1fr);
          }

          .contact-copy {
            padding-right: 0;
          }
        }

        @media (max-width: 768px) {
          .navbar-inner {
            padding: 14px 16px;
          }

          .hamburger {
            display: flex;
          }

          .nav-links {
            position: absolute;
            top: 78px;
            left: 4%;
            right: 4%;
            flex-direction: column;
            gap: 14px;
            padding: 18px;
            border-radius: 20px;
            background: var(--nav-bg);
            border: 1px solid var(--border);
            display: none;
            box-shadow: var(--shadow);
          }

          .nav-links.open {
            display: flex;
          }

          .nav-desktop-cta {
            display: none;
          }

          .hero {
            min-height: 100vh;
            padding: 95px 0 40px;
            align-items: flex-end;
          }

          .hero-bg-image {
            object-position: center;
          }

          .hero-title {
            font-size: clamp(2.5rem, 10vw, 4rem);
            line-height: 1.05;
          }

          .hero-text {
            font-size: 1rem;
            line-height: 1.75;
          }

          .hero-actions {
            flex-direction: column;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
          }

          .stats-row,
          .contact-stats,
          .hero-bottom-cards {
            grid-template-columns: 1fr;
          }

          .project-image-wrap {
            height: 220px;
          }

          .section {
            padding: 75px 0;
          }

          .nav-right {
            gap: 8px;
          }

          .theme-toggle {
            width: 42px;
            height: 42px;
          }
        }
      `}</style>

      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="navbar-inner">
          <div className="logo">
            Tynoe<span>Chigz</span>
          </div>

          <ul className={`nav-links ${menuOpen ? "open" : ""}`}>
            <li><button onClick={() => scrollTo("home")}>Home</button></li>
            <li><button onClick={() => scrollTo("about")}>About</button></li>
            <li><button onClick={() => scrollTo("services")}>Services</button></li>
            <li><button onClick={() => scrollTo("skills")}>Skills</button></li>
            <li><button onClick={() => scrollTo("projects")}>Projects</button></li>
            <li><button onClick={() => scrollTo("contact")}>Contact</button></li>
          </ul>

          <div className="nav-right">
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              <i className={`fas ${theme === "dark" ? "fa-sun" : "fa-moon"}`}></i>
            </button>

            <button className="nav-cta nav-desktop-cta" onClick={() => scrollTo("contact")}>
              Hire Me
            </button>

            <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </nav>

      <section className="hero" id="home">
        <div className="hero-bg">
          <img src={dev1} alt="Tinotenda Chigumira" className="hero-bg-image" />
          <div className="hero-overlay"></div>
          <div className="hero-overlay-2"></div>
        </div>

        <div className="container hero-content-wrap">
          <div className="hero-left modern-hero">
            <div className="hero-badge">
              <i className="fas fa-code"></i> Full-Stack Developer • Harare, Zimbabwe
            </div>

            <h1 className="hero-title">
              {typedTitle}
              <span className="cursor">|</span>
            </h1>

            <p className="hero-text">
              I design and build clean, responsive, and production-ready web and mobile
              applications with elegant interfaces, strong backend systems, and
              solutions that solve real problems.
            </p>

            <div className="hero-actions">
              <button className="btn-primary" onClick={() => scrollTo("projects")}>
                View Projects
              </button>
              <button className="btn-secondary" onClick={() => scrollTo("contact")}>
                Let’s Talk
              </button>
            </div>

            <div className="hero-bottom-cards">
              <div className="hero-glass-card">
                <h3>50+</h3>
                <p>Projects & concepts built</p>
              </div>

              <div className="hero-glass-card">
                <h3>3+</h3>
                <p>Years building digital products</p>
              </div>

              <div className="hero-glass-card">
                <h3>100%</h3>
                <p>Focused on modern UI quality</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="about">
        <div className="container" style={{ textAlign: "center" }}>
          <h2 className="section-title">About Me</h2>
          <p className="section-subtitle">
            I enjoy transforming ideas into polished products with modern interfaces,
            efficient backend architecture, and responsive experiences across devices.
          </p>

          <div className="about-grid">
            <div className="about-card glass">
              <h3>Frontend Focus</h3>
              <p>
                I build sleek interfaces using React, Next.js, Tailwind CSS, and modern
                UI patterns that make applications feel premium and user-friendly.
              </p>
            </div>

            <div className="about-card glass">
              <h3>Backend Strength</h3>
              <p>
                I create secure and scalable APIs using Node.js, Express, Java, and
                databases like PostgreSQL and SQL Server for reliable system performance.
              </p>
            </div>

            <div className="about-card glass">
              <h3>Product Mindset</h3>
              <p>
                I care about how products look, feel, and solve real problems, combining
                clean design with practical engineering.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="services">
        <div className="container" style={{ textAlign: "center" }}>
          <h2 className="section-title">Services</h2>
          <p className="section-subtitle">
            I help build complete digital products from idea to deployment.
          </p>

          <div className="services-grid">
            <div className="service-card glass">
              <div className="service-icon">
                <i className="fas fa-laptop-code"></i>
              </div>
              <h3>Web Development</h3>
              <p>
                Modern responsive websites, dashboards, and portals built with clean UI
                and strong performance.
              </p>
            </div>

            <div className="service-card glass">
              <div className="service-icon">
                <i className="fas fa-server"></i>
              </div>
              <h3>Backend Systems</h3>
              <p>
                Scalable APIs, authentication, databases, and admin functionality for
                production-ready applications.
              </p>
            </div>

            <div className="service-card glass">
              <div className="service-icon">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <h3>Mobile Apps</h3>
              <p>
                Cross-platform mobile app solutions with modern UX and smooth usability.
              </p>
            </div>

            <div className="service-card glass">
              <div className="service-icon">
                <i className="fas fa-palette"></i>
              </div>
              <h3>UI / UX Design</h3>
              <p>
                Elegant interface design with attention to clarity, visual quality, and
                user-centered interaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="skills">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "34px" }}>
            <h2 className="section-title">Skills & Stack</h2>
            <p className="section-subtitle">
              A focused look at the technologies and tools I use to build polished digital products.
            </p>
          </div>

          <div className="skills-layout">
            <div className="skills-menu glass">
              {Object.keys(skillsData).map((category) => (
                <button
                  key={category}
                  className={activeCategory === category ? "active" : ""}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            <div>
              <div className="skills-panel glass">
                {animatedSkills.map((skill, index) => (
                  <div className="skill-item" key={index}>
                    <div className="skill-top">
                      <span>{skill.name}</span>
                      <span>{skill.level}%</span>
                    </div>
                    <div className="skill-bar">
                      <div className="skill-fill" style={{ width: `${skill.level}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="stats-row glass">
                <div className="stat-box">
                  <h4>{statsData[activeCategory].technologies}</h4>
                  <p>Technologies</p>
                </div>
                <div className="stat-box">
                  <h4>{statsData[activeCategory].categories}</h4>
                  <p>Categories</p>
                </div>
                <div className="stat-box">
                  <h4>{statsData[activeCategory].avg}</h4>
                  <p>Avg Proficiency</p>
                </div>
                <div className="stat-box">
                  <h4>{statsData[activeCategory].years}</h4>
                  <p>Years Experience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="projects">
        <div className="container" style={{ textAlign: "center" }}>
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">
            A showcase of products and platforms I have designed and developed.
          </p>

          <div className="projects-wrap">
            <Slider {...sliderSettings}>
              {projects.map((project, index) => (
                <div key={index}>
                  <div
                    className="project-card glass"
                    onClick={() => window.open(project.link, "_blank")}
                  >
                    <div className="project-image-wrap">
                      <img src={project.image} alt={project.title} />
                      <span className="project-badge">{project.badge}</span>
                    </div>

                    <div className="project-body">
                      <h3>{project.title}</h3>
                      <p>{project.description}</p>

                      <div className="tech-stack">
                        {project.tech.map((item, idx) => (
                          <span key={idx}>{item}</span>
                        ))}
                      </div>

                      <button
                        className="btn-mini"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(project.link, "_blank");
                        }}
                      >
                        Visit Project
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="quote-box glass">
            <p>
              “I believe great software should not only work well, but also look
              refined, feel intuitive, and create real value for the people using it.”
            </p>
            <h4>Tinotenda Tinos Chigumira • Full-Stack Developer</h4>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="info-grid">
            <div className="info-card glass">
              <h3>My Projects</h3>
              <p>
                Explore systems and websites I have built using modern technologies
                across frontend, backend, and mobile.
              </p>
              <button className="btn-mini" onClick={() => scrollTo("projects")}>
                View Projects
              </button>
            </div>

            <div className="info-card glass">
              <h3>Technical Skills</h3>
              <p>
                See my current stack across React, Node.js, Flutter, Java, databases,
                deployment tools, and interface design.
              </p>
              <button className="btn-mini" onClick={() => scrollTo("skills")}>
                View Skills
              </button>
            </div>

            <div className="info-card glass">
              <h3>Let’s Work</h3>
              <p>
                Need a website, portal, dashboard, or mobile app? Let’s turn your idea
                into a clean and functional product.
              </p>
              <button className="btn-mini" onClick={() => scrollTo("contact")}>
                Contact Me
              </button>
            </div>
          </div>
        </div>
      </section>

    <section className="section" id="contact">
  <div className="container contact-grid">
    <div className="contact-copy">
      <span className="contact-pill">● Get In Touch</span>
     <h2 className="contact-title">
  Let’s build <span>{typedContact}</span> together.
</h2>
      <p>
        Ready to bring your next idea to life? I help build modern websites,
        portals, dashboards, and mobile apps with a clean design direction and
        strong technical foundation.
      </p>

      <div className="contact-list">
        <div>
          <i className="fas fa-envelope"></i>
          <span>chigumira21@gmail.com</span>
        </div>
        <div>
          <i className="fab fa-whatsapp"></i>
          <span>+263 716 292 923</span>
        </div>
        <div>
          <i className="fas fa-map-marker-alt"></i>
          <span>Harare, Zimbabwe</span>
        </div>
      </div>
    </div>

    <div className="contact-card glass">
      <h3>Start a Project</h3>
      <p>Pick your preferred way to reach me.</p>

      <div className="contact-actions">
        <a href="mailto:chigumira21@gmail.com" className="contact-link primary">
          <i className="fas fa-envelope"></i> Send Email
        </a>

        <a
          href="https://wa.me/263716292923"
          target="_blank"
          rel="noopener noreferrer"
          className="contact-link secondary"
        >
          <i className="fab fa-whatsapp"></i> WhatsApp Me
        </a>

        <a
          href="/Tinotenda_Chigumira_CV.pdf"
          download
          className="contact-link secondary"
        >
          <i className="fas fa-download"></i> Download CV
        </a>
      </div>

      <div className="contact-stats">
        <div>
          <strong>24h</strong>
          <span>Response Time</span>
        </div>
        <div>
          <strong>50+</strong>
          <span>Projects</span>
        </div>
        <div>
          <strong>Modern</strong>
          <span>UI Quality</span>
        </div>
      </div>
    </div>
  </div>
</section>

      <footer className="footer">
        <div className="container">
          <div className="footer-grid glass">
            <div className="footer-brand">
              <h3>Tynoe Chigz</h3>
              <p>
                Full-stack developer building modern digital experiences with premium UI,
                strong backend systems, and clean deployment-ready solutions.
              </p>

              <div className="footer-socials">
                <a href="https://github.com" target="_blank" rel="noreferrer" aria-label="GitHub">
                  <i className="fab fa-github"></i>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="mailto:chigumira21@gmail.com" aria-label="Email">
                  <i className="fas fa-envelope"></i>
                </a>
              </div>
            </div>

            <div className="footer-links">
              <h4>Navigation</h4>
              <p><a href="#home">Home</a></p>
              <p><a href="#about">About</a></p>
              <p><a href="#skills">Skills</a></p>
              <p><a href="#projects">Projects</a></p>
            </div>

            <div className="footer-links">
              <h4>Services</h4>
              <p>Web Development</p>
              <p>Backend Systems</p>
              <p>Mobile Apps</p>
              <p>UI / UX Design</p>
            </div>

            <div className="footer-links">
              <h4>Contact</h4>
              <p>Harare, Zimbabwe</p>
              <p>chigumira21@gmail.com</p>
              <p>+263 716 292 923</p>
            </div>
          </div>

         <div className="footer-bottom">
  <p className="copyright">
    © {new Date().getFullYear()} Tinotenda Chigumira. All rights reserved.
  </p>

  <span className="footer-line"></span>

  <p className="powered">
    Powered by <strong>EiffelTech</strong>
  </p>
</div>
        </div>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "var(--bg)",
    color: "var(--text)",
    transition: "background 0.35s ease, color 0.35s ease",
  },
};

export default App;
